/**
 * MessageOpsButton — talent-facing "Message iEvntür" CTA for active
 * shift screens (ClockInOut, ShiftDetail, Bookings). Opens a modal
 * with the ops conversation, preserves the draft to IndexedDB across
 * auth refresh / PWA restart / redirect, and records every send
 * attempt (success + failure) via the audit endpoint.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { LifeBuoy, X, Send, Loader2, AlertCircle } from 'lucide-react';
import api from '../../utils/api';
import {
  saveDraft, loadDraft, clearDraft,
  enqueueSend, drainQueue, removeFromQueue,
} from '../../utils/onsiteMessageDrafts';

export function MessageOpsButton({ booking, className = '' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold shadow-md ${className}`}
        data-testid={`message-ops-btn-${booking?.id || 'noid'}`}
      >
        <LifeBuoy className="w-4 h-4" />
        Message iEvntür
      </button>
      {open && (
        <MessageOpsModal
          booking={booking}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function MessageOpsModal({ booking, onClose }) {
  const [threadId, setThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);

  // Bootstrap: open or create the ops thread for this shift.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post('/messages/onsite/start', {
          booking_id: booking.id,
          shift_id: booking.shift_id,
        });
        if (cancelled) return;
        setThreadId(r.data.thread_id);
        const stored = await loadDraft(r.data.thread_id);
        if (stored) setDraft(stored);
        const thr = await api.get(`/messages/onsite/thread/${r.data.thread_id}`);
        if (!cancelled) setMessages(thr.data.messages || []);
      } catch (e) {
        setError('Could not open the ops conversation. Your message is still safe — it will be saved locally.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [booking.id, booking.shift_id]);

  // Persist draft on every change.
  useEffect(() => {
    if (threadId) saveDraft(threadId, draft);
  }, [draft, threadId]);

  // Scroll to bottom on new messages.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Drain any offline-queued sends on modal open.
  useEffect(() => {
    (async () => {
      const queued = await drainQueue();
      for (const q of queued) {
        try {
          await api.post('/messages/onsite/send', q.payload);
          await removeFromQueue(q.id);
        } catch { /* leave for next attempt */ }
      }
    })();
  }, []);

  const handleSend = useCallback(async () => {
    const content = (draft || '').trim();
    if (!content || !threadId) return;
    setSending(true);
    setError(null);
    const payload = {
      thread_id: threadId,
      content,
      booking_id: booking.id,
      shift_id: booking.shift_id,
      client_request_id: (window.crypto?.randomUUID?.() ||
        `oc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`),
      client_context: {
        path: typeof window !== 'undefined' ? window.location.pathname : null,
        ua: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        online: typeof navigator !== 'undefined' ? navigator.onLine : true,
      },
    };
    try {
      const r = await api.post('/messages/onsite/send', payload);
      // Optimistic-append the message locally.
      setMessages((prev) => [
        ...prev,
        {
          id: r.data.message_id,
          content, sender_role: 'talent',
          delivery_status: 'sent', priority: 'onsite_urgent',
          created_at: new Date().toISOString(), is_own: true,
        },
      ]);
      setDraft('');
      await clearDraft(threadId);
      toast.success(`Sent — iEvntür has been notified (${r.data.recipients_fanned_out} recipient(s))`);
    } catch (e) {
      // System failure — draft stays put + queue for retry + audit-log
      // it server-side so the talent is not marked non-compliant for
      // a platform failure.
      await enqueueSend({ payload });
      try {
        await api.post('/messages/onsite/record-attempt', {
          thread_id: threadId,
          channel: 'talent_send',
          status: 'client_error',
          error_code: e?.response?.status ? String(e.response.status) : 'network',
          error_message: e?.message || 'Unknown network error',
          client_context: payload.client_context,
        }).catch(() => { /* even the audit call may fail — the draft + queue survive */ });
      } catch { /* noop */ }
      setError('We could not deliver your message right now. It is saved and will retry automatically. iEvntür has been notified this attempt failed — this will never count against you.');
    } finally {
      setSending(false);
    }
  }, [draft, threadId, booking.id, booking.shift_id]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      data-testid="message-ops-modal"
      role="dialog"
    >
      <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[92vh] border-t-4 border-orange-500">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-orange-50">
          <div>
            <h3 className="text-base font-bold text-orange-900 flex items-center gap-1.5">
              <LifeBuoy className="w-4 h-4" />
              iEvntür Onsite Support
            </h3>
            <p className="text-[11px] text-orange-800/80 mt-0.5">
              Priority channel — we'll be with you shortly.
            </p>
          </div>
          <button
            type="button" onClick={onClose}
            className="text-orange-900 hover:bg-orange-100 rounded-full p-1.5"
            data-testid="message-ops-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2" data-testid="message-ops-body">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-orange-600" />
            </div>
          )}
          {!loading && messages.length === 0 && (
            <div className="text-center py-6 text-sm text-gray-500">
              You haven't messaged iEvntür yet for this shift. Type below.
            </div>
          )}
          {messages.map((m) => (
            <div
              key={m.id}
              className={`flex ${m.is_own ? 'justify-end' : 'justify-start'}`}
              data-testid={`msg-${m.id}`}
            >
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                m.is_own
                  ? 'bg-orange-600 text-white rounded-br-sm'
                  : 'bg-slate-100 text-slate-900 rounded-bl-sm'
              }`}>
                <div>{m.content}</div>
                <div className={`text-[10px] mt-1 ${m.is_own ? 'text-orange-100' : 'text-slate-500'}`}>
                  {m.sender_role === 'talent' ? 'You' : 'iEvntür Ops'}
                  {m.created_at ? ` · ${new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
                  {m.delivery_status === 'failed' && ' · failed'}
                </div>
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        {/* Error banner */}
        {error && (
          <div className="mx-4 mb-2 px-3 py-2 rounded border border-amber-300 bg-amber-50 text-amber-900 text-xs flex gap-2" data-testid="message-ops-error">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Composer */}
        <div className="p-3 border-t bg-white">
          <div className="flex gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="What's going on onsite? (message iEvntür directly)"
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
              rows={2}
              maxLength={4000}
              data-testid="message-ops-textarea"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={!draft.trim() || sending || !threadId}
              className="self-end inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white text-sm font-semibold"
              data-testid="message-ops-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send
            </button>
          </div>
          <p className="text-[10px] text-slate-500 mt-1.5">
            iEvntür is auto-notified by SMS + email. Your draft is saved on this device.
          </p>
        </div>
      </div>
    </div>
  );
}

export default MessageOpsButton;
